/**
 * ONE PERSON'S DOOR ONTO HOME, ON HER CARD (ops #19, step 5; `docs/seams/home.md` section 6).
 *
 * Home is the household's own page, `home.faithfullykept.com`, a system of its own. Ops holds only the
 * office's decision that a person on the household may see it there: invited, on home once she has
 * signed in with the address ops holds for her, or removed. The invitation is an email home sends, so
 * the button is offered only to a person with an email address, on a household that has not ended, and
 * only where this deployment is connected to home (`me.homeSeam`). That is UX and never the control:
 * the office's routes refuse a deployment not connected to home, a household that has ended, a person
 * with no email, one marked not to be emailed, and a cleaner, whatever is drawn here.
 *
 * INVITING AGAIN REPLACES THE DOOR, which is how the office changes what she may do or which homes she
 * sees: the old one ends `superseded` in the same write. REMOVING asks first, because it also cancels
 * whatever she asked through home that nobody in the office has answered yet, and is offered on any
 * live door whatever `me.homeSeam` says: the office's route never asks it, and deleting the household
 * is refused `still_on_home` until every door is shut, so a door made before this deployment lost its
 * connection must still be removable from here.
 */
import { useState } from 'react';
import { MEMBERSHIP_ROLES, type MembershipRole } from '@fk/shared';
import {
  ApiError,
  inviteToHome,
  removeFromHome,
  resendToHome,
  type ContactRow,
  type MembershipRow,
  type SiteRow,
} from '../api.ts';
import { dayOf, formatDay } from '../format.ts';
import {
  MEMBERSHIP_REVOKE_REASON_LABELS,
  MEMBERSHIP_ROLE_LABELS,
  MEMBERSHIP_STATE_LABELS,
} from '../labels.ts';
import { Pill } from './AppShell.tsx';
import { Button, SelectField } from './Field.tsx';
import { ErrorNotice } from './Notice.tsx';

/** The door a person's card speaks of: her live one, else the last one that ended, else none. */
export function doorOf(
  memberships: readonly MembershipRow[],
  contactId: string,
): MembershipRow | null {
  const hers = memberships.filter((door) => door.contactId === contactId);
  return hers.find((door) => door.state !== 'revoked') ?? hers[0] ?? null;
}

const TONE = { invited: 'gold', granted: 'sage', revoked: 'quiet' } as const;

function homeName(site: SiteRow): string {
  return site.label ?? site.addressLine1;
}

export function HomeMembership({
  clientId,
  contact,
  sites,
  door,
  homeSeam,
  householdEnded,
  onDone,
}: {
  clientId: string;
  contact: ContactRow;
  sites: readonly SiteRow[];
  door: MembershipRow | null;
  homeSeam: boolean;
  /** Ending the household closed every door onto it, and the office's route refuses a new one. */
  householdEnded: boolean;
  onDone: (sentence: string) => void;
}) {
  const [step, setStep] = useState<'shut' | 'invite' | 'remove'>('shut');
  const [role, setRole] = useState<MembershipRole>(door?.role ?? 'client');
  const [homes, setHomes] = useState<readonly string[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const live = door !== null && door.state !== 'revoked';
  const field = (name: string) => `home-${contact.id}-${name}`;

  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      setStep('shut');
      onDone(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  function invite() {
    const chosen = role === 'member' && homes.length > 0 ? homes : null;
    void run(
      () => inviteToHome(clientId, { contactId: contact.id, role, homes: chosen }),
      `Invited. Home emails ${contact.displayName} how to sign in with ${contact.email ?? 'their address'}.`,
    );
  }

  function remove() {
    if (!door) return;
    void run(
      () => removeFromHome(clientId, door.id),
      `Removed. ${contact.displayName} no longer sees this household at home.`,
    );
  }

  if (!door && !homeSeam) return null;

  return (
    <div className="rounded-lg border border-navy/15 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs tracking-wide text-navy/55 uppercase">Home</p>
        {door ? <Pill tone={TONE[door.state]}>{MEMBERSHIP_STATE_LABELS[door.state]}</Pill> : null}
      </div>
      {door?.state === 'invited' ? (
        <p className="mt-1 text-xs text-navy/65">
          Invited {formatDay(dayOf(door.invitedAt))}, as a {door.role}. They have not signed in yet.
        </p>
      ) : null}
      {door?.state === 'granted' && door.boundAt ? (
        <p className="mt-1 text-xs text-navy/65">
          Signed in first on {formatDay(dayOf(door.boundAt))}, as a {door.role}.
        </p>
      ) : null}
      {door?.state === 'revoked' && door.revokedAt ? (
        <p className="mt-1 text-xs text-navy/65">
          Since {formatDay(dayOf(door.revokedAt))}
          {door.revokeReason ? `: ${MEMBERSHIP_REVOKE_REASON_LABELS[door.revokeReason]}.` : '.'}
        </p>
      ) : null}

      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="Not written down" message={problem} />
        </div>
      ) : null}

      {step === 'shut' && (homeSeam || live) ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {!homeSeam ? null : householdEnded ? (
            <p className="text-xs text-navy/65">
              This household has ended, so there is no home page to invite them to.
            </p>
          ) : contact.email ? (
            <Button tone="quiet" onClick={() => setStep('invite')}>
              {live ? 'Invite again' : 'Invite to home'}
            </Button>
          ) : (
            <p className="text-xs text-navy/65">
              The invitation is an email, so add their email address to invite them to home.
            </p>
          )}
          {live ? (
            <Button tone="quiet" onClick={() => setStep('remove')}>
              Remove from home
            </Button>
          ) : null}
        </div>
      ) : null}

      {step === 'invite' ? (
        <div className="mt-3 rounded-lg border border-navy/20 p-3">
          <p className="text-sm">
            Home emails {contact.displayName} at {contact.email} how to sign in. Nothing goes from
            ops.{live ? ' This replaces the door they have now.' : ''}
          </p>
          <div className="mt-3">
            <SelectField
              id={field('role')}
              label="What they may do"
              value={role}
              onChange={setRole}
              options={MEMBERSHIP_ROLES}
              toLabel={(value) => MEMBERSHIP_ROLE_LABELS[value]}
            />
          </div>
          {role === 'member' && sites.length > 1 ? (
            <fieldset className="mb-3">
              <legend className="text-sm font-medium">Which homes</legend>
              <p className="text-xs text-navy/65">Tick none for every home of the household.</p>
              <div className="mt-2 flex flex-col gap-2">
                {sites.map((site) => (
                  <label
                    key={site.id}
                    htmlFor={field(`site-${site.id}`)}
                    className="flex min-h-11 desk:min-h-9 cursor-pointer items-center gap-3"
                  >
                    <input
                      id={field(`site-${site.id}`)}
                      type="checkbox"
                      checked={homes.includes(site.id)}
                      onChange={(event) =>
                        setHomes((now) =>
                          event.target.checked
                            ? [...now, site.id]
                            : now.filter((one) => one !== site.id),
                        )
                      }
                      className="h-5 w-5 shrink-0 accent-navy"
                    />
                    <span className="text-sm">{homeName(site)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button onClick={invite} disabled={busy}>
              {busy ? 'Writing it down' : 'Invite to home'}
            </Button>
            <Button tone="quiet" onClick={() => setStep('shut')} disabled={busy}>
              Never mind
            </Button>
          </div>
        </div>
      ) : null}

      {step === 'remove' ? (
        <div className="mt-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
          <p className="text-sm">
            Remove {contact.displayName} from home? They no longer see this household there, and
            anything they asked through home that the office has not answered is cancelled. Why: the
            office&apos;s decision.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={remove} disabled={busy}>
              {busy ? 'Writing it down' : 'Yes, remove'}
            </Button>
            <Button tone="quiet" onClick={() => setStep('shut')} disabled={busy}>
              Never mind
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * "SEND IT ALL AGAIN", ON THE HOUSEHOLD: its whole copy enqueued for home afresh, the repair for a push
 * that gave up or a first copy that stopped part way. Offered only while somebody on the household is
 * on home, since there is nobody else to send it to.
 */
export function SendItAllAgain({
  clientId,
  memberships,
  homeSeam,
  onDone,
}: {
  clientId: string;
  memberships: readonly MembershipRow[];
  homeSeam: boolean;
  onDone: (sentence: string) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!homeSeam || !memberships.some((door) => door.state === 'granted')) return null;

  async function resend() {
    setBusy(true);
    setProblem(null);
    try {
      const { enqueued } = await resendToHome(clientId);
      onDone(
        `Queued for home: ${enqueued} records. They go over the next few hours, a few every five minutes.`,
      );
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {problem ? <ErrorNotice title="Not sent" message={problem} /> : null}
      <div>
        <Button tone="quiet" onClick={() => void resend()} disabled={busy}>
          {busy ? 'Queueing it' : 'Send it all again'}
        </Button>
      </div>
    </div>
  );
}
